import type { Dataset } from '../../shared/types';
import { eur0, eur2, num0, pctF, roasF } from './format';
import { roasColor } from './theme';

/** Single legible "spend" colour (the overview chart's light taupe is too faint to stand alone). */
const SPEND_COLOR = '#9C7838';

export const RANGES = ['Heute', '7 Tage', '30 Tage', 'KW', 'Monate', 'YTD', 'Zeitraum'] as const;
export type Range = (typeof RANGES)[number];
/** Pills shown in the header (Zeitraum is implicit — the date pickers are always visible). */
export const RANGE_PILLS: Range[] = ['Heute', '7 Tage', '30 Tage', 'KW', 'Monate', 'YTD'];
export type Gran = 'day' | 'week' | 'month';

export const TABS = [
  { id: 'cockpit', label: 'Cockpit' },
  { id: 'kanaele', label: 'Kanäle' },
  { id: 'werbung', label: 'Werbung' },
  { id: 'produkte', label: 'Produkte' },
  { id: 'vergleich', label: 'Vergleich' },
  { id: 'daten', label: 'Daten' },
] as const;
export type TabId = (typeof TABS)[number]['id'];

/** Countries below this period revenue are noise (Matomo bots etc.) and hidden. */
const COUNTRY_MIN_REVENUE = 1000;

/** Which metric the Cockpit chart plots. 'roas' = the combined overview chart. */
export type ChartMetric = 'roas' | 'umsatz' | 'spend' | 'orders' | 'aov';

export interface DashboardState {
  range: Range;
  selChannels: string[];
  selCountries: string[];
  /** For range === 'Zeitraum' (custom date window). */
  from?: string;
  to?: string;
  /** Cockpit chart metric (selected by clicking a KPI card). Default 'roas'. */
  metric?: ChartMetric;
  /** Werbung: country selected in the ROAS-pro-Land box → drives the ad-channel breakdown. null = all. */
  adCountry?: string | null;
  /** Cockpit: bucket index clicked in the chart → scopes the right-panel breakdown to that day/KW/month. null = whole window. */
  selBucket?: number | null;
}

export interface Kpi {
  label: string;
  value: string;
  delta?: string;
  tone: 'pos' | 'neg' | 'neutral';
  subnote?: string;
}

/** Period-table column keys. */
export type PeriodColKey = 'umsatz' | 'spend' | 'roas' | 'orders' | 'aov';

/** Fixed Umsatz/Werbeausgaben/ROAS columns (Werbung tab + Cockpit ROAS view). */
export const ROAS_PERIOD_COLS: { label: string; key: PeriodColKey }[] = [
  { label: 'Umsatz', key: 'umsatz' },
  { label: 'Werbeausgaben', key: 'spend' },
  { label: 'ROAS', key: 'roas' },
];

/** A hover column for a chart: full-height hit area (x..x+w) + tooltip text lines. */
export interface TooltipCol {
  x: number;
  w: number;
  cx: number;
  lines: { t: string; c?: string }[];
}

export interface DashboardVM {
  rangeCaption: string;
  periodTitle: string;
  periodHead: string;
  sources: string;
  amazonAdsSpendPending: boolean;
  channelsFiltered: boolean;
  roasNote: string;

  hero: { roas: string; deltaLabel: string; deltaColor: string; note: string };
  kpis: Kpi[];

  channels: { name: string; color: string; umsatz: string; orders: string; share: string }[];
  channelsDetail: {
    name: string; color: string; umsatz: string; orders: string; aov: string;
    sharePct: string; spark: string; neu: boolean;
  }[];

  chart: {
    bars: { cx: number; rx: number; ry: number; rh: number; sx: number; sy: number; sh: number; w: number; label: string; showLabel: boolean }[];
    linePoints: string;
    roasDots: { cx: number; cy: number }[];
    gridLines: { y: number }[];
    yLabels: { y: number; label: string }[];
    yLabelsR: { y: number; label: string }[];
    cols: TooltipCol[];
  };
  /** Cockpit chart metric currently selected ('roas' = the combined chart above). */
  cockpitMetric: ChartMetric;
  /** Single-series chart for the selected non-ROAS metric (null when metric === 'roas'). */
  metricChart: { bars: { x: number; y: number; h: number; w: number; cx: number; label: string; showLabel: boolean }[]; gridLines: { y: number; label: string }[]; color: string; cols: TooltipCol[] } | null;
  metricTitle: string;
  /** Right-panel breakdown for the selected metric (per channel, or per country for spend/ROAS).
   *  When a chart bucket is clicked, it scopes to that day/KW/month (bucketLabel set). */
  breakdown: { title: string; sublabel: string; perCountry: boolean; bucketLabel: string | null; rows: { name: string; color: string; value: string; sub: string; barW: string; pending: boolean }[] };
  /** Cockpit: resolved/clamped selected bucket index (null = whole window) — drives the chart highlight. */
  selBucket: number | null;
  periodTable: { label: string; umsatz: string; spend: string; roas: string; orders: string; aov: string }[];
  /** Which columns the period table shows, following the selected metric. */
  periodCols: { label: string; key: PeriodColKey }[];
  /** Werbung per-day table (Umsatz/Werbeausgaben/ROAS), scoped to the selected ad-country. */
  werbungPeriodTable: { label: string; umsatz: string; spend: string; roas: string; orders: string; aov: string }[];

  adsKpis: Kpi[];
  /** ROAS shown on the Werbung Zielband ("aktuell"), scoped to the selected ad-country. */
  adsRoas: string;
  gauge: { bandLeft: string; bandWidth: string; markerLeft: string; ticks: { label: string; left: string }[]; hasRoas: boolean };
  countryRoas: { name: string; w: string; label: string; color: string; pending: boolean }[];
  roasBand: { left: string; width: string };
  spendRevByCountry: { name: string; umsatz: string; spend: string; umW: string; spW: string }[];
  /** Ad-spend split by ad channel (Werbung right box), reactive to the selected country. */
  adChannels: { scopeLabel: string; total: string; selectedCountry: string | null; rows: { name: string; color: string; value: string; share: string; barW: string; pending: boolean }[] };
  /** Werbung left box: one horizontal stacked bar per country = ad spend by ad
   *  channel over the active window (absolute € scale, widest bar = 100 %), with
   *  the country's ROAS at the right. Clickable → scopes the rest of the page. */
  spendByCountryChannels: {
    channels: { name: string; color: string }[];
    maxLabel: string;
    rows: { name: string; total: string; barW: string; roasLabel: string; roasColor: string; noSpend: boolean;
            segments: { name: string; color: string; value: string; share: string; w: string }[] }[];
  };
  /** Werbung bottom: 100 %-stacked spend mix per period (day / KW / month) + a
   *  period × channel cost table, newest-first. Scoped like the rest of the tab. */
  spendMix: {
    channels: { name: string; color: string }[];
    bars: { x: number; w: number; cx: number; label: string; showLabel: boolean; segs: { y: number; h: number; color: string }[] }[];
    yLabels: { y: number; label: string }[];
    cols: TooltipCol[];
    table: { label: string; cells: string[]; total: string }[];
  };
  /** Werbung per-period chart: Umsatz bars (left € axis) + ROAS line (right × axis)
   *  with the green target corridor drawn on the ROAS axis. */
  roasWeek: {
    bandTop: number; bandH: number;
    bars: { x: number; w: number; y: number; h: number; cx: number; label: string; showLabel: boolean }[];
    linePoints: string;
    roasDots: { cx: number; cy: number }[];
    gridLines: { y: number }[];
    yLabels: { y: number; label: string }[];
    yLabelsR: { y: number; label: string }[];
    cols: TooltipCol[];
  };

  productKpis: Kpi[];
  amazonProductsPending: boolean;
  /** Column labels of the product table — only the channels selected in the filter. */
  productChannels: string[];
  productSales: { sku: string; name: string; cells: string[]; total: string }[];
  /** True when the country filter excludes at least one meaningful country. */
  countriesFiltered: boolean;
  heatmap: { products: string[]; rows: { country: string; total: string; cells: { label: string; empty: boolean; alpha: number }[] }[] };
  /** True when the heatmap follows the active date window (daily cube present). */
  heatmapRangeAware: boolean;
  /** True when the heatmap follows the channel filter (full product cube present). */
  heatmapChannelAware: boolean;
  /** True when the product table applies the country filter exactly (full cube). */
  productExactFilters: boolean;
  /** Cockpit "Umsatz nach Land": net revenue (incl shipping) per selected country. */
  revenueLand: { rows: { name: string; value: string; sub: string; barW: string }[]; total: string; note: string };

  imports: { source: string; lastSuccess: string; status: string; statusLabel: string; detail: string }[];
  refreshedAt: string;
}

const MONTHS_DE = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
const MONTHS_LONG = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

const d = (s: string) => new Date(s + 'T00:00:00Z');
const deDate = (s: string) => { const x = d(s); return `${x.getUTCDate()}. ${MONTHS_LONG[x.getUTCMonth()]} ${x.getUTCFullYear()}`; };
const deShort = (s: string) => { const x = d(s); return `${x.getUTCDate()}.${x.getUTCMonth() + 1}.`; };

function isoWeek(date: Date): number {
  const t = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return Math.ceil(((t.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

interface Bucket { label: string; idxs: number[] }

/** Day indices whose date falls within [from, to] (inclusive). */
function idxBetween(dates: string[], from: string, to: string): number[] {
  const out: number[] = [];
  dates.forEach((ds, i) => { if (ds >= from && ds <= to) out.push(i); });
  return out;
}

/** Group day indices into day / ISO-week / month buckets. */
function groupBuckets(dates: string[], idxs: number[], gran: Gran): Bucket[] {
  if (gran === 'day') return idxs.map((i) => ({ label: deShort(dates[i]), idxs: [i] }));
  const m = new Map<string, number[]>();
  for (const i of idxs) {
    const dt = d(dates[i]);
    const key = gran === 'week' ? `${dt.getUTCFullYear()}-W${isoWeek(dt)}` : `${dt.getUTCFullYear()}-${dt.getUTCMonth()}`;
    (m.get(key) ?? m.set(key, []).get(key)!).push(i);
  }
  return [...m.entries()].map(([key, ix]) => ({
    label: gran === 'week' ? 'KW' + key.split('W')[1] : MONTHS_DE[Number(key.split('-')[1])],
    idxs: ix,
  }));
}

const autoGran = (spanDays: number): Gran => (spanDays <= 45 ? 'day' : spanDays <= 182 ? 'week' : 'month');

function sumOver(map: Record<string, number[]>, countries: string[], idxs: number[]): number {
  return countries.reduce((a, c) => a + idxs.reduce((b, i) => b + (map[c]?.[i] || 0), 0), 0);
}

function spark(vals: number[]): string {
  const w = 120, h = 34, pad = 4;
  const max = Math.max(...vals), min = Math.min(...vals);
  const span = max - min || 1, n = vals.length || 1;
  return vals
    .map((v, i) => {
      const x = pad + ((w - 2 * pad) * i) / Math.max(1, n - 1);
      const y = h - pad - ((h - 2 * pad) * (v - min)) / span;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
}

export function meaningfulCountries(data: Dataset): string[] {
  return data.countries.filter((c) => (data.revenueByCountry[c] ?? 0) >= COUNTRY_MIN_REVENUE);
}

/** The last COMPLETE day in the dataset. The dataset's final day is the current day and
 *  partial — ad spend posts through the day before all of its sales land, which would crater
 *  that day's ROAS. So every range except "Heute" ends here (= yesterday). Only treated as
 *  partial when the final day is actually today in the report timezone; a stale dataset whose
 *  last day is already in the past is returned unchanged. */
export function lastCompleteDay(data: Dataset): string {
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Berlin' });
  if (data.until < today) return data.until; // dataset already ends on a finished day
  const i = data.dates.indexOf(data.until);
  return i > 0 ? data.dates[i - 1] : data.until; // step back one day; keep if it's the only day
}

/** Sales channels the Meta/Google web spend is attributed to. */
const WEB_CHANNELS = ['cosyworld.com', 'WooCommerce'];

/** Ad channels in display order, with their colours (Werbung tab). */
const AD_CHANNEL_ORDER = ['Meta', 'Google Ads', 'Amazon Ads', 'OTTO Ads'];
const AD_CHANNEL_COLOR: Record<string, string> = { Meta: '#4A7FB5', 'Google Ads': '#E7A569', 'OTTO Ads': '#C9483B', 'Amazon Ads': '#6E8B7B' };
const adChannelsSorted = (names: Iterable<string>) =>
  [...new Set(names)].sort((a, b) => (AD_CHANNEL_ORDER.indexOf(a) + 99) % 99 - (AD_CHANNEL_ORDER.indexOf(b) + 99) % 99 || a.localeCompare(b));

/** Total revenue of one named sales channel over a set of day indices. */
function chRevOver(data: Dataset, name: string, idxs: number[]): number {
  const ch = data.channels.find((c) => c.name === name);
  return ch ? idxs.reduce((a, i) => a + (ch.revenueDaily[i] || 0), 0) : 0;
}

/** Selected channels' share of total channel revenue over a set of day indices —
 *  the approximation used for per-country revenue when the channel×country cube
 *  is absent (matches the Cockpit aggregation fallback). */
function channelRevShare(data: Dataset, selChannels: string[], idxs: number[]): number {
  const sel = data.channels
    .filter((c) => selChannels.includes(c.name))
    .reduce((a, c) => a + idxs.reduce((b, i) => b + (c.revenueDaily[i] || 0), 0), 0);
  const all = data.channels.reduce((a, c) => a + idxs.reduce((b, i) => b + (c.revenueDaily[i] || 0), 0), 0);
  return all > 0 ? sel / all : 0;
}

/** Weight of one AD channel under the current sales-channel selection:
 *  Amazon Ads → Amazon, OTTO Ads → OTTO, Meta/Google Ads → the web shops
 *  (cosyworld.com + WooCommerce, split by their revenue share over `idxs`).
 *  eBay runs no ads. With all channels selected every weight is 1. */
function adChannelWeights(data: Dataset, selChannels: string[], idxs: number[]): (adCh: string) => number {
  const webSel = WEB_CHANNELS.filter((n) => selChannels.includes(n));
  const webTotal = WEB_CHANNELS.reduce((a, n) => a + chRevOver(data, n, idxs), 0);
  const webShare =
    webSel.length === WEB_CHANNELS.length ? 1
    : webSel.length === 0 ? 0
    : webTotal > 0 ? webSel.reduce((a, n) => a + chRevOver(data, n, idxs), 0) / webTotal
    : 0.5;
  return (adCh: string) =>
    adCh === 'Amazon Ads' ? (selChannels.includes('Amazon') ? 1 : 0)
    : adCh === 'OTTO Ads' ? (selChannels.includes('OTTO') ? 1 : 0)
    : webShare;
}

/** One country's ad spend per ad channel over `idxs`. Real daily rows from the
 *  channel×country×day cube when the bundle has it; otherwise the PERIOD snapshot
 *  scaled by the country's window/period spend ratio (the pre-2026-09-11 behaviour,
 *  which mis-stated the mix as soon as budgets moved between channels). */
function adSplitOver(data: Dataset, c: string, idxs: number[]): Record<string, number> {
  const cube = data.spendByChannelByCountryDaily;
  if (cube) {
    const out: Record<string, number> = {};
    for (const [ch, byCountry] of Object.entries(cube)) {
      const arr = byCountry[c];
      if (!arr) continue;
      const v = idxs.reduce((a, i) => a + (arr[i] || 0), 0);
      if (v > 0) out[ch] = v;
    }
    return out;
  }
  const split = data.spendByChannelByCountry?.[c];
  if (!split) return {};
  const snapTotal = Object.values(split).reduce((a, v) => a + v, 0) || 1;
  const ratio = sumOver(data.spendByCountryDaily, [c], idxs) / snapTotal;
  return Object.fromEntries(Object.entries(split).map(([ch, v]) => [ch, v * ratio]));
}

/** Per-country share of tracked ad spend attributable to the selected sales
 *  channels, from that country's ad-channel split over `idxs`. Countries without a
 *  split are treated like pure Meta/Google (web) spend. */
function spendAttribution(data: Dataset, selChannels: string[], idxs: number[]): (c: string) => number {
  const w = adChannelWeights(data, selChannels, idxs);
  const cache: Record<string, number> = {};
  return (c: string) => {
    if (cache[c] != null) return cache[c];
    const split = adSplitOver(data, c, idxs);
    const tot = Object.values(split).reduce((a, v) => a + v, 0);
    return (cache[c] = tot > 0 ? Object.entries(split).reduce((a, [ch, v]) => a + v * w(ch), 0) / tot : w('Meta'));
  };
}

export function computeDashboard(data: Dataset, state: DashboardState): DashboardVM {
  const { range } = state;
  const band = data.targetBand;

  // ---- window + buckets ----
  // The date range [from, to] is the MASTER window for the whole dashboard and is
  // always applied. The active pill only chooses the bucket granularity:
  //   Heute / 7 Tage / 30 Tage → daily   ·   KW → weekly   ·   Monate / YTD → monthly.
  // (Heute/7T/30T/YTD also set the window itself — done in the UI.) Editing the date
  // pickers re-buckets the same way, so KW/Monate react to whatever range is chosen.
  const from = state.from || data.since;
  let to = state.to || data.until;
  // Exclude the partial current day from every range except "Heute": ad spend posts before
  // the day's sales complete, so including it craters ROAS. Cap the window to the last
  // complete day (no-op when the window already ends earlier; never inverts the window).
  if (range !== 'Heute') {
    const lc = lastCompleteDay(data);
    if (to > lc && lc >= from) to = lc;
  }
  const idxs = idxBetween(data.dates, from, to);
  const gran: Gran =
    range === 'KW' ? 'week'
    : range === 'Monate' || range === 'YTD' ? 'month'
    : range === 'Heute' || range === '7 Tage' || range === '30 Tage' ? 'day'
    : autoGran(idxs.length); // Zeitraum / custom edit
  const buckets: Bucket[] = groupBuckets(data.dates, idxs, gran);
  const windowIdx = buckets.flatMap((b) => b.idxs);

  // Cockpit: a bucket clicked in the chart scopes the right-panel breakdown to that
  // single day / KW / month. Clamped to the current bucket count (a stale index from a
  // prior range resolves to null = whole window).
  const bucketSel = state.selBucket != null && state.selBucket >= 0 && state.selBucket < buckets.length ? state.selBucket : null;
  const bdIdx = bucketSel != null ? buckets[bucketSel].idxs : windowIdx;
  const bdLabel = bucketSel != null ? buckets[bucketSel].label : null;

  const allCountries = data.countries;
  const sumCh = (ch: { revenueDaily: number[]; ordersDaily: number[] }, idxs: number[], key: 'revenueDaily' | 'ordersDaily') =>
    idxs.reduce((a, i) => a + (ch[key][i] || 0), 0);
  const sumCountry = sumOver;

  // Real per-(channel, country, day) revenue / orders. Returns null when the dataset
  // predates these maps (→ caller falls back to the old country-share approximation).
  const chRevCube = data.channelRevByCountryDaily;
  const chOrdCube = data.channelOrdersByCountryDaily;
  const chCube = (
    cube: Record<string, Record<string, number[]>> | undefined,
    chName: string,
    countries: string[],
    idxs: number[],
  ): number | null => {
    const m = cube?.[chName];
    if (!m) return null;
    let s = 0;
    for (const c of countries) {
      const arr = m[c];
      if (arr) for (const i of idxs) s += arr[i] || 0;
    }
    return s;
  };

  // ---- channel filter → spend attribution & revenue share (global filter) ----
  // Ad spend is tracked per AD channel and country, not per sales channel. To make
  // the header channel filter global (incl. Werbeausgaben and ROAS), spend is
  // attributed to sales channels — see adChannelWeights(). With all channels
  // selected every share is exactly 1, so the unfiltered numbers are unchanged.
  const allCh = state.selChannels.length === data.channels.length;
  const adW = allCh ? () => 1 : adChannelWeights(data, state.selChannels, windowIdx);
  const spendShare = allCh ? () => 1 : spendAttribution(data, state.selChannels, windowIdx);
  const spendOf = (countries: string[], ix: number[]) =>
    countries.reduce((a, c) => a + sumCountry(data.spendByCountryDaily, [c], ix) * spendShare(c), 0);
  // Selected channels' global revenue share — the approximation for per-country
  // revenue values (the channel×country cube is absent in current datasets).
  const selChShare = allCh ? 1 : channelRevShare(data, state.selChannels, windowIdx);

  // ---- aggregate over a set of day indices ----
  // Sum each selected channel's REAL revenue/orders in the selected countries. Only
  // when the per-country maps are absent (old data) fall back to scaling the channel's
  // global total by the blended country share `cw` (the legacy approximation).
  // Ad spend is only tracked from `spendSince` on (revenue reaches further back). ROAS
  // must never divide pre-tracking revenue by spend, or older ranges look far better
  // than they were: ROAS uses only the days from spendSince, and periods entirely
  // before it show "—" instead of an "organic" ROAS.
  const spendFrom = data.spendSince ? Math.max(0, data.dates.indexOf(data.spendSince)) : 0;
  const tracked = (idxs: number[]) => (spendFrom > 0 ? idxs.filter((i) => i >= spendFrom) : idxs);

  const agg = (idxs: number[]): { umsatz: number; orders: number; spend: number; ctryRevSel: number; roas: number | null; untracked: boolean } => {
    const sel = data.channels.filter((c) => state.selChannels.includes(c.name));
    const ctryRevSel = sumCountry(data.revenueByCountryDaily, state.selCountries, idxs);
    const ctryRevAll = sumCountry(data.revenueByCountryDaily, allCountries, idxs);
    const cw = ctryRevAll > 0 ? ctryRevSel / ctryRevAll : 0;
    let umsatz = 0, orders = 0;
    for (const c of sel) {
      const rv = chCube(chRevCube, c.name, state.selCountries, idxs);
      if (rv != null) {
        umsatz += rv;
        orders += chCube(chOrdCube, c.name, state.selCountries, idxs) ?? 0;
      } else {
        umsatz += sumCh(c, idxs, 'revenueDaily') * cw;
        orders += sumCh(c, idxs, 'ordersDaily') * cw;
      }
    }
    const spendSel = spendOf(state.selCountries, idxs);
    // ROAS: with all channels the classic country-based figure; with a channel
    // filter active, filtered revenue ÷ attributed spend.
    const ti = tracked(idxs);
    if (ti.length !== idxs.length) {
      const t = ti.length ? agg(ti) : null;
      return { umsatz, orders, spend: spendSel, ctryRevSel, roas: t?.roas ?? null, untracked: !ti.length };
    }
    const revForRoas = allCh ? ctryRevSel : umsatz;
    return { umsatz, orders, spend: spendSel, ctryRevSel, roas: spendSel > 0 ? revForRoas / spendSel : null, untracked: false };
  };

  const cur = agg(windowIdx);

  // previous equal window (for deltas) — only if it fits within available data
  const winLen = windowIdx.length;
  const firstIdx = windowIdx[0] ?? 0;
  const prevIdx = firstIdx - winLen >= 0 ? Array.from({ length: winLen }, (_, k) => firstIdx - winLen + k) : null;
  const prev = prevIdx ? agg(prevIdx) : null;

  const deltaPct = (c: number, p: number | null | undefined): { delta?: string; tone: 'pos' | 'neg' | 'neutral' } => {
    if (p == null || p === 0) return { tone: 'neutral' };
    const pct = ((c - p) / p) * 100;
    const arrow = pct >= 0 ? '▲ ' : '▼ ';
    return { delta: arrow + (pct >= 0 ? '+' : '−') + Math.abs(pct).toFixed(1).replace('.', ',') + ' %', tone: pct >= 0 ? 'pos' : 'neg' };
  };

  const roasNote = allCh
    ? 'ROAS: länderbasiert · alle Kanäle'
    : 'Kanal-Auswahl aktiv — Werbeausgaben & ROAS nach Kanal-Attribution: Amazon Ads → Amazon, OTTO Ads → OTTO, Meta/Google → Webshops';
  const heroRoas = cur.roas != null ? roasF(cur.roas) : '—';
  const heroDelta = cur.roas != null && prev?.roas != null ? deltaPct(cur.roas, prev.roas) : { tone: 'neutral' as const };

  const hero = {
    roas: heroRoas,
    deltaLabel: heroDelta.delta ?? (cur.roas != null ? 'ggü. Vorperiode' : ''),
    deltaColor: heroDelta.tone === 'pos' ? '#2E9E6B' : heroDelta.tone === 'neg' ? '#CF4B3E' : '#8a8f94',
    note: data.amazonAdsSpendPending ? 'Net-Umsatz ÷ Spend · Amazon-Ads ausstehend' : 'Net-Umsatz ÷ Werbeausgaben',
  };

  const aov = cur.orders ? cur.umsatz / cur.orders : 0;
  const aovPrev = prev && prev.orders ? prev.umsatz / prev.orders : null;
  const kpis: Kpi[] = [
    { label: 'Umsatz', value: eur0(cur.umsatz), ...deltaPct(cur.umsatz, prev?.umsatz), subnote: 'vs. Vorp.' },
    { label: 'Werbeausgaben', value: eur0(cur.spend), ...deltaPct(cur.spend, prev?.spend), subnote: 'vs. Vorp.' },
    { label: 'Bestellungen', value: num0(Math.round(cur.orders)), ...deltaPct(cur.orders, prev?.orders), subnote: 'vs. Vorp.' },
    { label: 'Ø Warenkorb', value: eur2(aov), ...deltaPct(aov, aovPrev), subnote: 'vs. Vorp.' },
  ];

  // ---- per-channel (country-weighted) ----
  const ctryRevSelW = cur.ctryRevSel;
  const ctryRevAllW = sumCountry(data.revenueByCountryDaily, allCountries, windowIdx) || 1;
  const cwW = ctryRevSelW / ctryRevAllW;
  const chanRows = data.channels
    .filter((c) => state.selChannels.includes(c.name))
    .map((c) => {
      const rv = chCube(chRevCube, c.name, state.selCountries, windowIdx);
      return {
        name: c.name, color: c.color, neu: !!c.neu,
        umsatzNum: rv != null ? rv : sumCh(c, windowIdx, 'revenueDaily') * cwW,
        ordersNum: rv != null ? (chCube(chOrdCube, c.name, state.selCountries, windowIdx) ?? 0)
                              : sumCh(c, windowIdx, 'ordersDaily') * cwW,
        // Sparkline follows the country filter, like the totals above: per bucket the
        // channel's real revenue in the selected countries (cube), else the legacy
        // country-share approximation. Previously this was the unfiltered channel total.
        spark: spark(buckets.map((b) => {
          const brv = chCube(chRevCube, c.name, state.selCountries, b.idxs);
          return brv != null ? brv : sumCh(c, b.idxs, 'revenueDaily') * cwW;
        })),
      };
    });
  const maxChan = Math.max(...chanRows.map((c) => c.umsatzNum), 1);
  const totalChan = chanRows.reduce((a, c) => a + c.umsatzNum, 0) || 1;
  const channels = chanRows.map((c) => ({
    name: c.name, color: c.color, umsatz: eur0(c.umsatzNum), orders: num0(Math.round(c.ordersNum)),
    share: ((c.umsatzNum / maxChan) * 100).toFixed(1) + '%',
  }));
  const channelsDetail = chanRows.map((c) => ({
    name: c.name, color: c.color, umsatz: eur0(c.umsatzNum), orders: num0(Math.round(c.ordersNum)),
    aov: eur2(c.ordersNum ? c.umsatzNum / c.ordersNum : 0),
    sharePct: pctF((c.umsatzNum / totalChan) * 100), spark: c.spark, neu: c.neu,
  }));

  // ---- per-bucket series for the chart ----
  // noCost = revenue with zero ad spend → show ROAS as the revenue amount ("933×"),
  // not "—" (which reads as no return when it was simply organic / no ad cost).
  const pts = buckets.map((b) => {
    const a = agg(b.idxs);
    const noCost = a.spend <= 0 && a.umsatz > 0 && !a.untracked;
    return {
      label: b.label, umsatz: a.umsatz, spend: a.spend,
      roas: a.spend > 0 ? (a.roas ?? 0) : (noCost ? a.umsatz : 0),
      hasRoas: a.spend > 0 || noCost, noCost,
      orders: a.orders, aov: a.orders ? a.umsatz / a.orders : 0,
    };
  });
  // Format a ROAS cell: '—' if undefined; for no-cost-but-revenue show the revenue
  // as a rounded multiple ("933×"); else the normal ROAS.
  const roasCell = (p: { roas: number; hasRoas: boolean; noCost: boolean }): string =>
    !p.hasRoas ? '—' : p.noCost ? num0(Math.round(p.roas)) + '×' : roasF(p.roas);
  const periodTitle = gran === 'day' ? 'Tag' : gran === 'week' ? 'Kalenderwoche' : 'Monat';
  const periodHead = gran === 'day' ? 'Datum' : gran === 'week' ? 'KW' : 'Monat';
  const NW = pts.length || 1;

  const rawMax = Math.max(...pts.map((p) => Math.max(p.umsatz, p.spend)), 1);
  const step = rawMax > 50000 ? 20000 : rawMax > 10000 ? 10000 : rawMax > 2000 ? 2000 : 500;
  const maxRev = Math.max(step, Math.ceil(rawMax / step) * step);
  // ROAS axis scales to the data (with headroom) so the line never clips the frame.
  // Exclude no-cost points (their "ROAS" is a revenue amount and would blow the scale).
  const roasPeak = Math.max(...pts.filter((p) => !p.noCost).map((p) => p.roas), cur.roas ?? 0, 0);
  // Multiple of 4 (min 8) so the quarter-grid ROAS ticks are clean integers.
  const maxRoasAxis = Math.max(8, Math.ceil((roasPeak * 1.12) / 4) * 4);
  const X0 = 48, X1 = 784, Y1 = 280, plotH = 256;
  const groupW = (X1 - X0) / NW;
  const barW = Math.max(4, Math.min(16, groupW * 0.3));
  const labelEvery = Math.max(1, Math.ceil(NW / 9));
  const bars = pts.map((p, i) => {
    const cx = X0 + groupW * i + groupW / 2;
    const rh = (p.umsatz / maxRev) * plotH, sh = (p.spend / maxRev) * plotH;
    return { cx, rx: cx - barW - 1, ry: Y1 - rh, rh, sx: cx + 1, sy: Y1 - sh, sh, w: barW, label: p.label, showLabel: i % labelEvery === 0 || i === NW - 1 };
  });
  const roasY = (p: { roas: number }) => Y1 - (Math.min(p.roas, maxRoasAxis) / maxRoasAxis) * plotH;
  const linePoints = pts.map((p, i) => `${(X0 + groupW * i + groupW / 2).toFixed(1)},${roasY(p).toFixed(1)}`).join(' ');
  const roasDots = pts.map((p, i) => ({ cx: X0 + groupW * i + groupW / 2, cy: +roasY(p).toFixed(1) }));
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * maxRev);
  const gridLines = ticks.map((t) => ({ y: +(Y1 - (t / maxRev) * plotH).toFixed(1) }));
  const yLabels = ticks.map((t) => ({ y: +(Y1 - (t / maxRev) * plotH).toFixed(1), label: t === 0 ? '0' : '€' + Math.round(t / 1000) + 'k' }));
  const yLabelsR = [0, 0.25, 0.5, 0.75, 1].map((f) => ({ y: +(Y1 - f * plotH).toFixed(1), label: Math.round(f * maxRoasAxis) + '×' }));

  // Per-bucket hover columns (full-height hit area + tooltip lines) for the charts.
  const colFor = (i: number, gw: number, x0: number, lines: { t: string; c?: string }[]): TooltipCol => ({
    x: +(x0 + gw * i).toFixed(1), w: +gw.toFixed(1), cx: +(x0 + gw * i + gw / 2).toFixed(1), lines,
  });
  const comboCols: TooltipCol[] = pts.map((p, i) =>
    colFor(i, groupW, X0, [
      { t: p.label },
      { t: 'Umsatz ' + eur0(p.umsatz), c: '#9FC0E0' },
      { t: 'Werbeausgaben ' + eur0(p.spend), c: '#DCD4C8' },
      ...(p.hasRoas ? [{ t: 'ROAS ' + roasCell(p) + (p.noCost ? ' (organisch)' : ''), c: '#E7A569' }] : []),
    ]),
  );

  // ---- single-metric chart (when a KPI other than ROAS is selected) ----
  const metric: ChartMetric = state.metric ?? 'roas';
  const metricMeta: Record<Exclude<ChartMetric, 'roas'>, { title: string; color: string; unit: 'eur' | 'eur2' | 'count'; value: (p: (typeof pts)[number]) => number }> = {
    umsatz: { title: 'Umsatz', color: '#4A7FB5', unit: 'eur', value: (p) => p.umsatz },
    spend: { title: 'Werbeausgaben', color: SPEND_COLOR, unit: 'eur', value: (p) => p.spend },
    orders: { title: 'Bestellungen', color: '#6E8B7B', unit: 'count', value: (p) => p.orders },
    aov: { title: 'Ø Warenkorb', color: '#B07F3B', unit: 'eur2', value: (p) => p.aov },
  };
  const niceStep = (x: number) => {
    if (x <= 0) return 1;
    const mag = Math.pow(10, Math.floor(Math.log10(x)));
    const n = x / mag;
    return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * mag;
  };
  const eurAxis = (t: number) =>
    t === 0 ? '0' : t >= 1000 ? '€' + (t / 1000).toLocaleString('de-DE', { maximumFractionDigits: 1 }) + 'k' : '€' + Math.round(t);
  let metricChart: { bars: { x: number; y: number; h: number; w: number; cx: number; label: string; showLabel: boolean }[]; gridLines: { y: number; label: string }[]; color: string; cols: TooltipCol[] } | null = null;
  let metricTitle = '';
  if (metric !== 'roas') {
    const m = metricMeta[metric];
    metricTitle = m.title;
    const vals = pts.map(m.value);
    const peak = Math.max(...vals, 0);
    // Scale from the real peak (not a 1-floor). Count metrics use an integer step
    // so quarter-ticks never collapse to duplicate labels. All-zero series → flat 0 baseline.
    const rawStep = niceStep(peak / 4);
    const step = m.unit === 'count' ? Math.max(1, Math.round(rawStep)) : rawStep;
    const maxV = peak > 0 ? step * 4 : 0;
    const mBarW = Math.max(4, Math.min(22, groupW * 0.5));
    const fmtAxis = (t: number) => (m.unit === 'count' ? (t === 0 ? '0' : num0(Math.round(t))) : eurAxis(t));
    const fmtVal = (v: number) => (m.unit === 'count' ? num0(Math.round(v)) : m.unit === 'eur2' ? eur2(v) : eur0(v));
    metricChart = {
      color: m.color,
      bars: pts.map((p, i) => {
        const v = m.value(p);
        const h = maxV > 0 ? (v / maxV) * plotH : 0;
        const cx = X0 + groupW * i + groupW / 2;
        return { x: +(cx - mBarW / 2).toFixed(1), y: +(Y1 - h).toFixed(1), h: +h.toFixed(1), w: mBarW, cx: +cx.toFixed(1), label: p.label, showLabel: i % labelEvery === 0 || i === NW - 1 };
      }),
      // peak 0 → single baseline label; otherwise four clean quarter ticks.
      gridLines: maxV > 0
        ? [0, 0.25, 0.5, 0.75, 1].map((f) => ({ y: +(Y1 - f * plotH).toFixed(1), label: fmtAxis(f * maxV) }))
        : [{ y: Y1, label: '0' }],
      cols: pts.map((p, i) => colFor(i, groupW, X0, [{ t: p.label }, { t: m.title + ' ' + fmtVal(m.value(p)), c: m.color }])),
    };
  }

  // Table is newest-first (latest date on top) so the reader sees the current day
  // without scrolling; the chart above stays chronological (it uses `pts` directly).
  const periodTable = pts
    .slice()
    .reverse()
    .map((p) => ({
      label: p.label,
      umsatz: eur0(p.umsatz),
      spend: p.spend > 0 ? eur0(p.spend) : '—',
      roas: roasCell(p),
      orders: num0(Math.round(p.orders)),
      aov: p.aov ? eur2(p.aov) : '—',
    }));
  // Table columns follow the selected metric so the table matches the chart above.
  const PERIOD_COLS: Record<ChartMetric, { label: string; key: PeriodColKey }[]> = {
    roas: ROAS_PERIOD_COLS,
    umsatz: [{ label: 'Umsatz', key: 'umsatz' }, { label: 'Bestellungen', key: 'orders' }],
    spend: [{ label: 'Werbeausgaben', key: 'spend' }, { label: 'Umsatz', key: 'umsatz' }],
    orders: [{ label: 'Bestellungen', key: 'orders' }, { label: 'Umsatz', key: 'umsatz' }],
    aov: [{ label: 'Ø Warenkorb', key: 'aov' }, { label: 'Bestellungen', key: 'orders' }],
  };
  const periodCols = PERIOD_COLS[metric];

  // ---- Werbung ----
  // The country picked in the ROAS-pro-Land box scopes the WHOLE Werbung page —
  // KPIs, the ROAS-Zielband gauge, the channel split and the per-day chart all
  // reflect that single country. (adCountry must be one of the active selCountries,
  // else it resolves to null = all selected countries.) ROAS is country revenue ÷
  // country spend; with no tracked spend it's null (shown "—", marker hidden), which
  // matches the ROAS-pro-Land bars right beside the gauge.
  const adCountry = state.adCountry && state.selCountries.includes(state.adCountry) ? state.adCountry : null;
  const aggCountry = (country: string, ids: number[]) => {
    const rev = sumCountry(data.revenueByCountryDaily, [country], ids) * selChShare;
    const sp = sumCountry(data.spendByCountryDaily, [country], ids) * spendShare(country);
    const revT = sumCountry(data.revenueByCountryDaily, [country], tracked(ids)) * selChShare;
    return { spend: sp, ctryRevSel: rev, roas: sp > 0 ? revT / sp : null };
  };
  const adCur = adCountry ? aggCountry(adCountry, windowIdx) : cur;
  const adPrev = adCountry ? (prevIdx ? aggCountry(adCountry, prevIdx) : null) : prev;
  const adRoas = adCur.roas != null ? roasF(adCur.roas) : '—';
  const adRoasDelta = adCur.roas != null && adPrev?.roas != null ? deltaPct(adCur.roas, adPrev.roas) : { tone: 'neutral' as const };

  const adsKpis: Kpi[] = [
    { label: adCountry ? 'Werbeausgaben' : 'Werbeausgaben gesamt', value: eur0(adCur.spend), ...deltaPct(adCur.spend, adPrev?.spend), subnote: 'vs. Vorperiode' },
    { label: 'ROAS (Net, länderbasiert)', value: adRoas, ...(adRoasDelta.delta ? adRoasDelta : { tone: 'neutral' as const }), subnote: `Zielband ${band.min.toFixed(1).replace('.', ',')}–${band.max.toFixed(1).replace('.', ',')}×` },
    // Without a country scope, the filtered revenue is the channel sum (cur.umsatz);
    // cur.ctryRevSel is the unfiltered country total and only correct with all channels.
    { label: `Umsatz (${adCountry ?? 'gewählte Länder'})`, value: eur0(adCountry ? adCur.ctryRevSel : allCh ? cur.ctryRevSel : cur.umsatz), tone: 'neutral', subnote: data.amazonAdsSpendPending ? 'Amazon-Ads ausstehend' : allCh ? 'alle Kanäle' : 'gewählte Kanäle' },
  ];

  const gMin = 2, gSpan = 3;
  const gp = (v: number) => ((Math.max(gMin, Math.min(5, v)) - gMin) / gSpan) * 100;
  const gauge = {
    hasRoas: adCur.roas != null,
    bandLeft: gp(band.min).toFixed(2) + '%',
    bandWidth: (gp(band.max) - gp(band.min)).toFixed(2) + '%',
    markerLeft: gp(adCur.roas ?? gMin).toFixed(2) + '%',
    ticks: [2, 3, 4, 5].map((v) => ({ label: v.toFixed(1).replace('.', ',') + '×', left: gp(v).toFixed(2) + '%' })),
  };

  const roasAxisMax = 6;
  const countryStats = state.selCountries
    .map((c) => {
      const rev = sumCountry(data.revenueByCountryDaily, [c], windowIdx) * selChShare;
      const sp = sumCountry(data.spendByCountryDaily, [c], windowIdx) * spendShare(c);
      // Ignore trivial spillover spend (< €50) so ROAS isn't a meaningless outlier.
      return { name: c, rev, spend: sp, roas: sp >= 50 ? rev / sp : null };
    })
    .filter((c) => c.spend > 0 || c.rev > 0);
  // Show every revenue-bearing country; those without (DTC) spend are marked
  // pending (their cost is Amazon Ads). Spend-tracked countries rank first.
  const countryRoas = [...countryStats]
    .sort((a, b) => b.spend - a.spend || b.rev - a.rev)
    .slice(0, 10)
    .map((c) =>
      c.roas != null
        ? { name: c.name, w: ((c.roas / roasAxisMax) * 100).toFixed(1) + '%', label: roasF(c.roas), color: roasColor(c.roas), pending: false }
        : c.spend > 0
          ? { name: c.name, w: '0%', label: 'ROAS n/a · geringer Spend', color: '#C9C4BD', pending: true }
          : { name: c.name, w: '0%', label: 'Amazon-Ads ausstehend', color: '#C9C4BD', pending: true },
    );
  const roasBand = { left: ((band.min / roasAxisMax) * 100).toFixed(2) + '%', width: (((band.max - band.min) / roasAxisMax) * 100).toFixed(2) + '%' };

  const maxCtry = Math.max(...countryStats.map((c) => c.rev), 1);
  const spendRevByCountry = [...countryStats]
    .sort((a, b) => b.rev - a.rev)
    .slice(0, 10)
    .map((c) => ({
      name: c.name, umsatz: eur0(c.rev), spend: c.spend > 0 ? eur0(c.spend) : '—',
      umW: ((c.rev / maxCtry) * 100).toFixed(1) + '%', spW: ((c.spend / maxCtry) * 100).toFixed(1) + '%',
    }));

  // ---- Werbung: ad-spend split by ad CHANNEL (Meta/Google/OTTO/Amazon) ----
  // Reactive to a country picked in the ROAS-pro-Land box (else all selected countries).
  // Real per-channel spend over the active window (adSplitOver), so the panel agrees
  // with the ROAS box AND with what the ad accounts actually spent in that window.
  const adScope = adCountry ? [adCountry] : state.selCountries;
  const adByChannel: Record<string, number> = {};
  for (const c of adScope) {
    // With a channel filter active, weight each ad channel by its attribution to
    // the selected sales channels (unattributed ad channels drop out).
    for (const [ch, amt] of Object.entries(adSplitOver(data, c, windowIdx))) adByChannel[ch] = (adByChannel[ch] ?? 0) + amt * adW(ch);
  }
  const adTotal = Object.values(adByChannel).reduce((a, v) => a + v, 0);
  const adChannelRows = Object.entries(adByChannel)
    .filter(([, v]) => v > 0.5)
    .sort((a, b) => b[1] - a[1])
    .map(([ch, v]) => ({
      name: ch, color: AD_CHANNEL_COLOR[ch] ?? '#9A948D', value: eur0(v),
      share: adTotal ? Math.round((v / adTotal) * 100) + ' %' : '—',
      barW: adTotal ? ((v / adTotal) * 100).toFixed(1) + '%' : '0%', pending: false,
    }));
  if (data.amazonAdsSpendPending) {
    adChannelRows.push({ name: 'Amazon Ads', color: '#C9C4BD', value: 'ausstehend', share: '—', barW: '0%', pending: true });
  }
  const adChannels = {
    scopeLabel: adCountry ?? 'Alle Länder',
    total: eur0(adTotal),
    selectedCountry: adCountry,
    rows: adChannelRows,
  };

  // ---- Werbung: spend by channel per COUNTRY (stacked bars, active window) ----
  const ctrySplits = state.selCountries.map((c) => {
    const split: Record<string, number> = {};
    for (const [ch, amt] of Object.entries(adSplitOver(data, c, windowIdx))) {
      const v = amt * adW(ch);
      if (v > 0.5) split[ch] = v;
    }
    const total = Object.values(split).reduce((a, v) => a + v, 0);
    const st = countryStats.find((x) => x.name === c);
    return { name: c, split, total, rev: st?.rev ?? 0, roas: st?.roas ?? null };
  }).filter((r) => r.total > 0 || r.rev > 0)
    .sort((a, b) => b.total - a.total || b.rev - a.rev)
    .slice(0, 10);
  const ctryMax = Math.max(...ctrySplits.map((r) => r.total), 1);
  const ctryChannels = adChannelsSorted(ctrySplits.flatMap((r) => Object.keys(r.split)));
  const spendByCountryChannels = {
    channels: ctryChannels.map((n) => ({ name: n, color: AD_CHANNEL_COLOR[n] ?? '#9A948D' })),
    maxLabel: eur0(ctryMax),
    rows: ctrySplits.map((r) => ({
      name: r.name,
      total: r.total > 0 ? eur0(r.total) : '—',
      barW: ((r.total / ctryMax) * 100).toFixed(1) + '%',
      roasLabel: r.roas != null ? roasF(r.roas) : r.total > 0 ? 'n/a' : 'kein Spend',
      roasColor: r.roas != null ? roasColor(r.roas) : '#A8A39D',
      noSpend: r.total <= 0,
      segments: ctryChannels.filter((n) => r.split[n]).map((n) => ({
        name: n, color: AD_CHANNEL_COLOR[n] ?? '#9A948D', value: eur0(r.split[n]),
        share: Math.round((r.split[n] / r.total) * 100) + ' %',
        w: ((r.split[n] / r.total) * 100).toFixed(2) + '%',
      })),
    })),
  };

  // ---- Werbung: 100 %-stacked spend mix per period + period × channel table ----
  const mixBuckets = buckets.map((b) => {
    const split: Record<string, number> = {};
    for (const c of adScope) {
      for (const [ch, amt] of Object.entries(adSplitOver(data, c, b.idxs))) {
        const v = amt * adW(ch);
        if (v > 0) split[ch] = (split[ch] ?? 0) + v;
      }
    }
    return { label: b.label, split, total: Object.values(split).reduce((a, v) => a + v, 0) };
  });
  const mixChannels = adChannelsSorted(mixBuckets.flatMap((b) => Object.keys(b.split)));
  const MX0 = 48, MX1 = 784, MY1 = 210, mPlotH = 192;
  const mGroupW = (MX1 - MX0) / NW;
  const mBarWidth = Math.max(4, Math.min(26, mGroupW * 0.62));
  const mCx = (i: number) => MX0 + mGroupW * i + mGroupW / 2;
  const spendMix = {
    channels: mixChannels.map((n) => ({ name: n, color: AD_CHANNEL_COLOR[n] ?? '#9A948D' })),
    bars: mixBuckets.map((b, i) => {
      let acc = 0;
      const segs = mixChannels.filter((n) => b.split[n]).map((n) => {
        const h = b.total > 0 ? (b.split[n] / b.total) * mPlotH : 0;
        acc += h;
        return { y: +(MY1 - acc).toFixed(1), h: +h.toFixed(1), color: AD_CHANNEL_COLOR[n] ?? '#9A948D' };
      });
      return { x: +(mCx(i) - mBarWidth / 2).toFixed(1), w: +mBarWidth.toFixed(1), cx: +mCx(i).toFixed(1), label: b.label, showLabel: i % labelEvery === 0 || i === NW - 1, segs };
    }),
    yLabels: [0, 0.25, 0.5, 0.75, 1].map((f) => ({ y: +(MY1 - f * mPlotH).toFixed(1), label: Math.round(f * 100) + ' %' })),
    cols: mixBuckets.map((b, i) =>
      colFor(i, mGroupW, MX0, [
        { t: b.label + ' · ' + (b.total > 0 ? eur0(b.total) : 'kein Spend') },
        ...mixChannels.filter((n) => b.split[n]).map((n) => ({
          t: n + ' ' + eur0(b.split[n]) + ' (' + Math.round((b.split[n] / b.total) * 100) + ' %)', c: AD_CHANNEL_COLOR[n] ?? '#9A948D',
        })),
      ]),
    ),
    table: mixBuckets.slice().reverse().map((b) => ({
      label: b.label,
      cells: mixChannels.map((n) => (b.split[n] ? eur0(b.split[n]) : '—')),
      total: b.total > 0 ? eur0(b.total) : '—',
    })),
  };

  // ---- Cockpit right-panel breakdown, keyed to the selected metric ----
  // Revenue/orders exist per channel (Amazon, cosyworld.com, …); spend & ROAS are
  // tracked per country, so those break down by Land instead.
  // Scoped to the clicked chart bucket (bdIdx) when one is selected, else the whole
  // window. bdIdx === windowIdx when nothing is selected, so the no-selection result
  // is identical to before.
  type BreakdownRow = { name: string; color: string; value: string; sub: string; barW: string; pending: boolean };
  const pctW = (v: number, mx: number) => ((Math.max(0, v) / (mx || 1)) * 100).toFixed(1) + '%';
  const bdCountryStats = bucketSel == null
    ? countryStats
    : state.selCountries
        .map((c) => {
          const rev = sumCountry(data.revenueByCountryDaily, [c], bdIdx) * selChShare;
          const sp = sumCountry(data.spendByCountryDaily, [c], bdIdx) * spendShare(c);
          return { name: c, rev, spend: sp, roas: sp >= 50 ? rev / sp : null };
        })
        .filter((c) => c.spend > 0 || c.rev > 0);
  const bdChanBase = (() => {
    const ctryRevSel = sumCountry(data.revenueByCountryDaily, state.selCountries, bdIdx);
    const ctryRevAll = sumCountry(data.revenueByCountryDaily, allCountries, bdIdx) || 1;
    const cw = ctryRevSel / ctryRevAll;
    return data.channels
      .filter((c) => state.selChannels.includes(c.name))
      .map((c) => {
        const rv = chCube(chRevCube, c.name, state.selCountries, bdIdx);
        const umsatz = rv != null ? rv : sumCh(c, bdIdx, 'revenueDaily') * cw;
        const orders = rv != null ? (chCube(chOrdCube, c.name, state.selCountries, bdIdx) ?? 0)
                                  : sumCh(c, bdIdx, 'ordersDaily') * cw;
        return { name: c.name, color: c.color, umsatz, orders, aov: orders ? umsatz / orders : 0 };
      });
  })();
  const breakdownRaw: { title: string; sublabel: string; perCountry: boolean; rows: BreakdownRow[] } = (() => {
    if (metric === 'spend') {
      const rows = bdCountryStats.filter((c) => c.spend > 0).sort((a, b) => b.spend - a.spend);
      const mx = Math.max(...rows.map((c) => c.spend), 1);
      return {
        title: 'Länder', sublabel: 'Werbeausgaben nach Land', perCountry: true,
        rows: rows.map((c) => ({ name: c.name, color: SPEND_COLOR, value: eur0(c.spend), sub: eur0(c.rev) + ' Umsatz', barW: pctW(c.spend, mx), pending: false })),
      };
    }
    if (metric === 'roas') {
      const rows = [...bdCountryStats].sort((a, b) => b.spend - a.spend || b.rev - a.rev);
      return {
        title: 'Länder', sublabel: 'ROAS nach Land', perCountry: true,
        rows: rows.map((c) =>
          c.roas != null
            ? { name: c.name, color: roasColor(c.roas), value: roasF(c.roas), sub: eur0(c.spend) + ' Spend', barW: pctW(Math.min(c.roas, roasAxisMax), roasAxisMax), pending: false }
            : c.spend > 0
              // tracked but trivial spend (<€50): ROAS not meaningful, but spend IS tracked
              ? { name: c.name, color: '#C9C4BD', value: 'ROAS n/a', sub: eur0(c.spend) + ' Spend', barW: '0%', pending: true }
              // no tracked spend → genuine Amazon-Ads / DTC pending case
              : { name: c.name, color: '#C9C4BD', value: 'Amazon-Ads ausstehend', sub: eur0(c.rev) + ' Umsatz', barW: '0%', pending: true },
        ),
      };
    }
    // channel-decomposable metrics
    const base = bdChanBase;
    if (metric === 'orders') {
      const rows = [...base].sort((a, b) => b.orders - a.orders);
      const mx = Math.max(...rows.map((r) => r.orders), 1);
      return { title: 'Kanäle', sublabel: 'Bestellungen', perCountry: false, rows: rows.map((r) => ({ name: r.name, color: r.color, value: r.orders > 0 && Math.round(r.orders) === 0 ? '<1' : num0(Math.round(r.orders)), sub: eur0(r.umsatz) + ' Umsatz', barW: pctW(r.orders, mx), pending: false })) };
    }
    if (metric === 'aov') {
      const rows = [...base].sort((a, b) => b.aov - a.aov);
      const mx = Math.max(...rows.map((r) => r.aov), 1);
      return { title: 'Kanäle', sublabel: 'Ø Warenkorb je Kanal', perCountry: false, rows: rows.map((r) => ({ name: r.name, color: r.color, value: eur2(r.aov), sub: num0(Math.round(r.orders)) + ' Bestellungen', barW: pctW(r.aov, mx), pending: false })) };
    }
    // umsatz (and any fallback)
    const rows = [...base].sort((a, b) => b.umsatz - a.umsatz);
    const mx = Math.max(...rows.map((r) => r.umsatz), 1);
    return { title: 'Kanäle', sublabel: 'Anteil am Umsatz', perCountry: false, rows: rows.map((r) => ({ name: r.name, color: r.color, value: eur0(r.umsatz), sub: num0(Math.round(r.orders)) + ' Bestellungen', barW: pctW(r.umsatz, mx), pending: false })) };
  })();
  const breakdown = { ...breakdownRaw, bucketLabel: bdLabel };

  // period ROAS — bar chart + table (Werbung). When a country is selected in the
  // ROAS-pro-Land box, scope the per-day series to that country (rev/spend of that
  // country); otherwise use the all-selected-countries series (pts). Werbung-only.
  const adSeries = adCountry
    ? buckets.map((b) => {
        const rev = sumCountry(data.revenueByCountryDaily, [adCountry], b.idxs) * selChShare;
        const sp = sumCountry(data.spendByCountryDaily, [adCountry], b.idxs) * spendShare(adCountry);
        const ti = tracked(b.idxs);
        const revT = ti.length === b.idxs.length ? rev : sumCountry(data.revenueByCountryDaily, [adCountry], ti) * selChShare;
        const noCost = sp <= 0 && rev > 0 && ti.length > 0;
        return { label: b.label, umsatz: rev, spend: sp, roas: sp > 0 ? revT / sp : (noCost ? rev : 0), hasRoas: sp > 0 || noCost, noCost };
      })
    : pts;
  // Umsatz bars on the left € axis show the revenue LEVEL; the ROAS line on the
  // right × axis shows efficiency — a high ROAS on a tiny revenue day is then
  // visible for what it is. ROAS axis scales from real-ROAS periods only; no-cost
  // (revenue/0) days pin the line to the top (tooltip marks them "organisch").
  const adRoasPeak = Math.max(...adSeries.filter((p) => !p.noCost).map((p) => p.roas), 0);
  const RX0 = 48, RX1 = 784, RY1 = 210, rPlotH = 192;
  const rMax = Math.max(8, Math.ceil((Math.max(adRoasPeak, band.max) * 1.12) / 4) * 4);
  const ryOf = (r: number) => RY1 - (Math.min(r, rMax) / rMax) * rPlotH;
  const adRevPeak = Math.max(...adSeries.map((p) => p.umsatz), 0);
  const adRevStep = niceStep(adRevPeak / 4);
  const adRevMax = adRevPeak > 0 ? adRevStep * 4 : 1;
  const rGroupW = (RX1 - RX0) / NW;
  const rBarW = Math.max(4, Math.min(22, rGroupW * 0.5));
  const rCx = (i: number) => RX0 + rGroupW * i + rGroupW / 2;
  const roasWeek = {
    bandTop: +ryOf(band.max).toFixed(1),
    bandH: +(ryOf(band.min) - ryOf(band.max)).toFixed(1),
    bars: adSeries.map((p, i) => {
      const h = (p.umsatz / adRevMax) * rPlotH;
      return { x: +(rCx(i) - rBarW / 2).toFixed(1), w: +rBarW.toFixed(1), y: +(RY1 - h).toFixed(1), h: +h.toFixed(1), cx: +rCx(i).toFixed(1), label: p.label, showLabel: i % labelEvery === 0 || i === NW - 1 };
    }),
    linePoints: adSeries.map((p, i) => `${rCx(i).toFixed(1)},${ryOf(p.hasRoas ? p.roas : 0).toFixed(1)}`).join(' '),
    roasDots: adSeries.map((p, i) => ({ cx: +rCx(i).toFixed(1), cy: +ryOf(p.hasRoas ? p.roas : 0).toFixed(1) })),
    gridLines: [0, 0.25, 0.5, 0.75, 1].map((f) => ({ y: +(RY1 - f * rPlotH).toFixed(1) })),
    yLabels: [0, 0.25, 0.5, 0.75, 1].map((f) => ({ y: +(RY1 - f * rPlotH).toFixed(1), label: eurAxis(f * adRevMax) })),
    yLabelsR: [0, 0.25, 0.5, 0.75, 1].map((f) => ({ y: +(RY1 - f * rPlotH).toFixed(1), label: Math.round(f * rMax) + '×' })),
    cols: adSeries.map((p, i) =>
      colFor(i, rGroupW, RX0, [
        { t: p.label },
        { t: 'Umsatz ' + eur0(p.umsatz), c: '#9FC0E0' },
        { t: 'ROAS ' + roasCell(p) + (p.noCost ? ' (organisch)' : ''), c: '#E7A569' },
        { t: 'Werbeausgaben ' + (p.spend > 0 ? eur0(p.spend) : '—'), c: '#DCD4C8' },
      ]),
    ),
  };
  // Per-day table under the Werbung chart, newest-first, scoped to the same series.
  const werbungPeriodTable = adSeries
    .slice()
    .reverse()
    .map((p) => ({
      label: p.label,
      umsatz: eur0(p.umsatz),
      spend: p.spend > 0 ? eur0(p.spend) : '—',
      roas: roasCell(p),
      orders: '—',
      aov: '—',
    }));

  // ---- Cockpit: Umsatz nach Land (net incl shipping, channel + range scoped) ----
  // Quick revenue-per-country view. Uses the real channel×country cube when channels are
  // filtered, else the all-channel country total. Both are net incl shipping.
  const landRev = (c: string) => {
    if (chRevCube && !allCh)
      return data.channels
        .filter((ch) => state.selChannels.includes(ch.name))
        .reduce((a, ch) => a + (chCube(chRevCube, ch.name, [c], windowIdx) ?? 0), 0);
    return sumCountry(data.revenueByCountryDaily, [c], windowIdx) * selChShare;
  };
  const landShip = (c: string) => {
    const m = data.shippingNetByCountryDaily?.[c];
    return m ? windowIdx.reduce((a, i) => a + (m[i] || 0), 0) * selChShare : 0;
  };
  const landStats = state.selCountries
    .map((c) => ({ name: c, rev: landRev(c), ship: landShip(c) }))
    .filter((r) => r.rev > 0.5)
    .sort((a, b) => b.rev - a.rev);
  const landMax = Math.max(...landStats.map((r) => r.rev), 1);
  const landTotal = landStats.reduce((a, r) => a + r.rev, 0);
  const landShipTotal = landStats.reduce((a, r) => a + r.ship, 0);
  const revenueLand = {
    rows: landStats.map((r) => ({
      name: r.name,
      value: eur0(r.rev),
      sub: pctF(landTotal ? (r.rev / landTotal) * 100 : 0) + ' Anteil'
        + (r.ship > 0.5 ? ` · inkl. ${eur0(r.ship)} Versand` : ''),
      barW: ((r.rev / landMax) * 100).toFixed(1) + '%',
    })),
    total: eur0(landTotal),
    note: landShipTotal > 0.5 ? `Netto · inkl. Versand (${eur0(landShipTotal)})` : 'Netto-Umsatz',
  };

  // ---- Produkte: shared filter state (used by heatmap + product table) ----
  const sumIdx = (arr: number[]) => windowIdx.reduce((a, i) => a + (arr[i] || 0), 0);
  const psChannels = (['cosyworld.com', 'OTTO', 'WooCommerce', 'Amazon'] as const).filter((k) =>
    state.selChannels.includes(k),
  );
  const countriesFiltered = state.selCountries.length < meaningfulCountries(data).length;
  const pcc = data.productChannelCountryDaily;

  // ---- Produkte heatmap (country-filtered; channel-filtered when the cube exists) ----
  // With productChannelCountryDaily present, every cell is the window sum over the
  // SELECTED channels — the heatmap follows the channel filter exactly. Otherwise:
  // range-aware via heatmap.daily (all channels), or the full-period matrix fallback.
  const hmCountries = data.heatmap.countries;
  const hmProducts = data.heatmap.products;
  const hmDaily = data.heatmap.daily; // daily[ci][pj] aligned to dates, or undefined
  const heatmapChannelAware = !!pcc;
  const heatmapRangeAware = Array.isArray(hmDaily) || heatmapChannelAware;
  const hmVal = (ci: number, pj: number) => {
    if (pcc) {
      const c = hmCountries[ci], p = hmProducts[pj];
      let s = 0;
      for (const ch of psChannels) {
        const arr = pcc[ch]?.[c]?.[p];
        if (arr) s += sumIdx(arr);
      }
      return s;
    }
    return hmDaily
      ? windowIdx.reduce((a, i) => a + (hmDaily[ci]?.[pj]?.[i] || 0), 0)
      : data.heatmap.matrix[ci]?.[pj] || 0;
  };

  const selHmRows = hmCountries.map((c, ci) => ({ c, ci })).filter(({ c }) => state.selCountries.includes(c));
  // In range-aware mode, drop product columns with no revenue anywhere in the window.
  const hmProdIdx = hmProducts
    .map((_, j) => j)
    .filter((j) => !heatmapRangeAware || selHmRows.some(({ ci }) => hmVal(ci, j) > 0));
  const heatmap = {
    products: hmProdIdx.map((j) => hmProducts[j]),
    rows: selHmRows
      .map(({ c, ci }) => {
        const vals = hmProdIdx.map((j) => hmVal(ci, j));
        const rowMax = Math.max(...vals, 1);
        return {
          country: c, total: num0(Math.round(vals.reduce((a, v) => a + v, 0))) + ' €',
          totalNum: vals.reduce((a, v) => a + v, 0),
          cells: vals.map((v) => (v ? { label: num0(Math.round(v)) + ' €', empty: false, alpha: +(0.15 + (v / rowMax) * 0.65).toFixed(2) } : { label: '—', empty: true, alpha: 0 })),
        };
      })
      // In range-aware mode a country with nothing in the window/selection is noise.
      .filter((r) => !heatmapRangeAware || r.totalNum > 0)
      .map(({ totalNum: _t, ...r }) => r),
  };

  // ---- product sales consolidation (range-aware, channel-filtered) ----
  // The table shows ONLY the channels selected in the header filter and totals only
  // those; deselected channels drop out entirely. (eBay has no product feed → never
  // a column.) With the full channel×country×product cube present, the country
  // filter applies exactly too; the legacy productDaily fallback has no country
  // dimension — flagged in the UI.
  // With "Alle Länder" selected, include even below-threshold countries the filter
  // list hides — the user asked for everything, not for "everything meaningful".
  const psCtrySel = countriesFiltered ? new Set(state.selCountries) : null;
  let psRows: { sku: string; name: string; vals: number[]; total: number }[];
  if (pcc) {
    const skuByName = new Map((data.productDaily ?? []).map((p) => [p.name, p.sku]));
    const fams = new Map<string, number[]>();
    psChannels.forEach((ch, k) => {
      for (const [c, byFam] of Object.entries(pcc[ch] ?? {})) {
        if (psCtrySel && !psCtrySel.has(c)) continue;
        for (const [f, arr] of Object.entries(byFam)) {
          const v = sumIdx(arr);
          if (!v) continue;
          const row = fams.get(f) ?? fams.set(f, Array(psChannels.length).fill(0)).get(f)!;
          row[k] += v;
        }
      }
    });
    psRows = [...fams.entries()].map(([name, vals]) => ({
      sku: skuByName.get(name) ?? name,
      name,
      vals,
      total: vals.reduce((a, v) => a + v, 0),
    }));
  } else {
    psRows = (data.productDaily ?? []).map((p) => {
      const vals = psChannels.map((k) => sumIdx(p[k]));
      return { sku: p.sku, name: p.name, vals, total: vals.reduce((a, v) => a + v, 0) };
    });
  }
  psRows = psRows.filter((p) => p.total > 0).sort((a, b) => b.total - a.total);
  const productSales = psRows.slice(0, 30).map((p) => ({
    sku: p.sku,
    name: p.name.length > 52 ? p.name.slice(0, 50) + '…' : p.name,
    cells: p.vals.map((v, i) => (psChannels[i] === 'Amazon' && data.amazonProductsPending ? '—' : eur0(v))),
    total: eur0(p.total),
  }));
  const psTotalRev = psRows.reduce((a, p) => a + p.total, 0);
  const productKpisNew: Kpi[] = [
    { label: 'Aktive Produkte', value: String(psRows.length), tone: 'neutral', subnote: 'mit Umsatz im Zeitraum' },
    { label: 'Produktumsatz', value: eur0(psTotalRev), tone: 'neutral', subnote: (allCh ? 'alle Kanäle' : 'gewählte Kanäle') + ' · netto' },
    { label: 'Top-Produkt', value: psRows[0] ? eur0(psRows[0].total) : '—', tone: 'neutral', subnote: psRows[0]?.name.slice(0, 28) ?? '' },
  ];

  // ---- caption ----
  const winDates = windowIdx.map((i) => data.dates[i]);
  const first = winDates[0], last = winDates[winDates.length - 1];
  const granLabel = gran === 'day' ? 'täglich' : gran === 'week' ? 'wöchentlich' : 'monatlich';
  const presetLabel =
    range === '7 Tage' ? 'Letzte 7 Tage'
    : range === '30 Tage' ? 'Letzte 30 Tage'
    : range === 'KW' ? 'Kalenderwochen'
    : range === 'Monate' ? 'Monate'
    : range === 'YTD' ? 'Jahr bis heute'
    : 'Zeitraum';
  const rangeCaption =
    range === 'Heute'
      ? `Heute · ${deDate(last)}`
      : `${presetLabel} · ${deShort(first)} – ${deShort(last)} ${d(last).getUTCFullYear()} · ${granLabel}`;

  const STATUS_LABEL: Record<string, string> = { ok: 'Aktuell', pending: 'Ausstehend', stale: 'Veraltet' };
  const imports = data.imports.map((im) => ({
    source: im.source,
    lastSuccess: im.lastSuccess ? `${deDate(im.lastSuccess.slice(0, 10))}, ${im.lastSuccess.slice(11, 16)} Uhr` : '—',
    status: im.status, statusLabel: STATUS_LABEL[im.status] ?? im.status, detail: im.detail ?? '',
  }));

  return {
    rangeCaption, periodTitle, periodHead, sources: data.sources,
    amazonAdsSpendPending: data.amazonAdsSpendPending, channelsFiltered: !allCh, roasNote,
    hero, kpis, channels, channelsDetail,
    chart: { bars, linePoints, roasDots, gridLines, yLabels, yLabelsR, cols: comboCols },
    cockpitMetric: metric, metricChart, metricTitle, breakdown, selBucket: bucketSel, periodTable, periodCols,
    adsKpis, adsRoas: adRoas, gauge, countryRoas, roasBand, spendRevByCountry, adChannels, spendByCountryChannels, spendMix, roasWeek, werbungPeriodTable,
    productKpis: productKpisNew, heatmap, heatmapRangeAware, heatmapChannelAware, revenueLand, imports,
    amazonProductsPending: data.amazonProductsPending,
    productChannels: [...psChannels], productSales, productExactFilters: !!pcc,
    countriesFiltered,
    refreshedAt: data.refreshedAt
      ? new Date(data.refreshedAt).toLocaleString('de-DE', { timeZone: 'Europe/Berlin', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) + ' Uhr'
      : '—',
  };
}

// ---------------------------------------------------------------------------
// Period comparison (Vergleich tab): revenue / ad cost / ROAS for an arbitrary
// date window, grouped either by country or by day/week/month.
// ---------------------------------------------------------------------------
export type GroupBy = 'country' | Gran;

export interface CompareRow {
  label: string;
  umsatz: string;
  spend: string;
  roas: string;
  umsatzNum: number;
  spendNum: number;
}
export interface CompareResult {
  rows: CompareRow[];
  total: CompareRow;
  caption: string;
}

const fmtRoas = (rev: number, sp: number) => (sp >= 50 ? roasF(rev / sp) : '—');

export function comparePeriod(
  data: Dataset,
  from: string,
  to: string,
  groupBy: GroupBy,
  selCountries: string[],
  selChannels?: string[],
): CompareResult {
  const idxs = idxBetween(data.dates, from, to);
  const allCh = !selChannels || selChannels.length === data.channels.length;
  const cube = data.channelRevByCountryDaily;
  // Channel filter applied like everywhere else: revenue via the channel×country
  // cube when present (else the global-share approximation), spend via the
  // ad-channel attribution (Amazon Ads → Amazon, OTTO Ads → OTTO, Meta/Google →
  // Webshops). ROAS is then filtered revenue ÷ attributed spend.
  const spendShare = allCh ? () => 1 : spendAttribution(data, selChannels!, idxs);
  const revOf = (countries: string[], ix: number[]): number => {
    const revAll = sumOver(data.revenueByCountryDaily, countries, ix);
    if (allCh) return revAll;
    if (cube) {
      let s = 0;
      for (const ch of selChannels!) {
        const m = cube[ch];
        if (!m) continue;
        for (const c of countries) {
          const arr = m[c];
          if (arr) for (const i of ix) s += arr[i] || 0;
        }
      }
      return s;
    }
    return revAll * channelRevShare(data, selChannels!, ix);
  };
  const mkRow = (label: string, ix: number[], countries: string[]): CompareRow => {
    const rev = revOf(countries, ix);
    const sp = countries.reduce((a, c) => a + sumOver(data.spendByCountryDaily, [c], ix) * spendShare(c), 0);
    return { label, umsatz: eur0(rev), spend: sp > 0 ? eur0(sp) : '—', roas: fmtRoas(rev, sp), umsatzNum: rev, spendNum: sp };
  };

  let rows: CompareRow[];
  if (groupBy === 'country') {
    rows = selCountries
      .map((c) => mkRow(c, idxs, [c]))
      .filter((r) => r.umsatzNum > 0 || r.spendNum > 0)
      .sort((a, b) => b.umsatzNum - a.umsatzNum);
  } else {
    // Date buckets newest-first so the latest period sits at the top of the table.
    rows = groupBuckets(data.dates, idxs, groupBy).map((b) => mkRow(b.label, b.idxs, selCountries)).reverse();
  }
  const total = mkRow('Gesamt', idxs, selCountries);
  const first = data.dates[idxs[0]] ?? from;
  const last = data.dates[idxs[idxs.length - 1]] ?? to;
  return { rows, total, caption: `${deDate(first)} – ${deDate(last)}` };
}

/** Clamp a date string to the dataset's [since, until] window. */
export function clampDate(data: Dataset, value: string): string {
  if (value < data.since) return data.since;
  if (value > data.until) return data.until;
  return value;
}

// ---------------------------------------------------------------------------
// Performance-Überblick (Vergleich tab): five fixed comparison blocks anchored
// to a reference day (default: the last complete day) —
//   1. that day vs. Ø of the last 4 same weekdays
//   2. the last complete week vs. Ø of the 4 weeks before it
//   3. the last complete month vs. Ø of the 4 months before it
//   4. the last complete month vs. the same month a year earlier
//   5. YTD vs. the same span of the previous year
// All figures follow the global channel/country filters (revenue via the
// channel×country cube, spend via ad-channel attribution — same rules as the
// rest of the dashboard). Benchmark periods that predate the data floor are
// dropped from the average (noted), or the whole benchmark shows "—".
// ---------------------------------------------------------------------------

const WEEKDAYS_DE = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

const shiftIso = (iso: string, days: number): string => {
  const dt = d(iso);
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
};
const monthStartIso = (y: number, m: number) => new Date(Date.UTC(y, m, 1)).toISOString().slice(0, 10);
const monthEndIso = (y: number, m: number) => new Date(Date.UTC(y, m + 1, 0)).toISOString().slice(0, 10);

export const OVERVIEW_COLS = ['ROAS', 'Umsatz', 'Werbeausgaben', 'Bestellungen', 'Ø Warenkorb'] as const;

export interface OverviewCells {
  roas: string; umsatz: string; spend: string; orders: string; aov: string;
}
export interface OverviewDelta { abs: string; pct: string; tone: 'pos' | 'neg' | 'neutral' }
export interface OverviewBlock {
  title: string;
  curLabel: string; curCaption: string;
  benchLabel: string; benchCaption: string;
  cur: OverviewCells | null;
  bench: OverviewCells | null;
  /** One delta per OVERVIEW_COLS column; null = not computable. */
  deltas: (OverviewDelta | null)[];
  note: string | null;
}

interface OvMetrics { umsatz: number; spend: number; orders: number; revRoas: number }
interface OvPeriod { from: string; to: string }

/** Umsatz/Spend/Bestellungen over a day-index window under the active channel +
 *  country filters — the same aggregation rules as computeDashboard's agg(). */
function ovAgg(data: Dataset, selCountries: string[], selChannels: string[], idxs: number[]): OvMetrics {
  const allCh = selChannels.length === data.channels.length;
  const cube = data.channelRevByCountryDaily;
  const ocube = data.channelOrdersByCountryDaily;
  const ctryRevSel = sumOver(data.revenueByCountryDaily, selCountries, idxs);
  const ctryRevAll = sumOver(data.revenueByCountryDaily, data.countries, idxs);
  const cw = ctryRevAll > 0 ? ctryRevSel / ctryRevAll : 0;
  let umsatz = 0, orders = 0;
  for (const ch of data.channels) {
    if (!selChannels.includes(ch.name)) continue;
    const m = cube?.[ch.name];
    if (m) {
      for (const c of selCountries) {
        const arr = m[c];
        if (arr) for (const i of idxs) umsatz += arr[i] || 0;
        const oarr = ocube?.[ch.name]?.[c];
        if (oarr) for (const i of idxs) orders += oarr[i] || 0;
      }
    } else {
      umsatz += idxs.reduce((a, i) => a + (ch.revenueDaily[i] || 0), 0) * cw;
      orders += idxs.reduce((a, i) => a + (ch.ordersDaily[i] || 0), 0) * cw;
    }
  }
  const spendShare = allCh ? () => 1 : spendAttribution(data, selChannels, idxs);
  const spend = selCountries.reduce((a, c) => a + sumOver(data.spendByCountryDaily, [c], idxs) * spendShare(c), 0);
  // ROAS: with all channels the classic country-based revenue, else the filtered channel sum.
  return { umsatz, spend, orders, revRoas: allCh ? ctryRevSel : umsatz };
}

export function compareOverview(
  data: Dataset,
  selCountries: string[],
  selChannels: string[],
  anchor: string,
): OverviewBlock[] {
  const covered = (p: OvPeriod) => p.from >= data.since && p.to <= data.until && p.from <= p.to;
  const overlaps = (p: OvPeriod) => p.to >= data.since && p.from <= data.until && p.from <= p.to;
  const metricsOf = (p: OvPeriod) => ovAgg(data, selCountries, selChannels, idxBetween(data.dates, p.from, p.to));

  const cellsOf = (m: OvMetrics): OverviewCells => ({
    roas: m.spend >= 50 ? roasF(m.revRoas / m.spend) : '—',
    umsatz: eur0(m.umsatz),
    spend: m.spend > 0 ? eur0(m.spend) : '—',
    orders: num0(Math.round(m.orders)),
    aov: m.orders > 0 ? eur2(m.umsatz / m.orders) : '—',
  });

  const mkDelta = (cur: number | null, ref: number | null, fmtAbs: (v: number) => string, lowerIsBetter = false): OverviewDelta | null => {
    if (cur == null || ref == null || ref === 0) return null;
    const abs = cur - ref;
    const pct = (abs / ref) * 100;
    const sign = abs >= 0 ? '+' : '−';
    const good = lowerIsBetter ? abs < 0 : abs > 0;
    return {
      abs: sign + fmtAbs(Math.abs(abs)),
      pct: sign + Math.abs(pct).toFixed(1).replace('.', ',') + ' %',
      tone: abs === 0 ? 'neutral' : good ? 'pos' : 'neg',
    };
  };
  const roasAbs = (v: number) => v.toFixed(2).replace('.', ',') + '×';
  const deltasOf = (cur: OvMetrics, ref: OvMetrics): (OverviewDelta | null)[] => [
    mkDelta(cur.spend >= 50 ? cur.revRoas / cur.spend : null, ref.spend >= 50 ? ref.revRoas / ref.spend : null, roasAbs),
    mkDelta(cur.umsatz, ref.umsatz, eur0),
    // Werbekosten: rising spend is shown red (lower is better).
    mkDelta(cur.spend > 0 ? cur.spend : null, ref.spend > 0 ? ref.spend : null, eur0, true),
    mkDelta(cur.orders, ref.orders, (v) => num0(Math.round(v))),
    mkDelta(cur.orders > 0 ? cur.umsatz / cur.orders : null, ref.orders > 0 ? ref.umsatz / ref.orders : null, eur2),
  ];

  /** Ø over the covered sub-periods; null when none is covered. Ratio metrics
   *  (ROAS/AOV) are invariant under the ÷n, so sums keep them exact. */
  const avgOf = (periods: OvPeriod[]): { m: OvMetrics; n: number; total: number } | null => {
    const cov = periods.filter(covered);
    if (cov.length === 0) return null;
    const sum = cov.map(metricsOf).reduce(
      (a, m) => ({ umsatz: a.umsatz + m.umsatz, spend: a.spend + m.spend, orders: a.orders + m.orders, revRoas: a.revRoas + m.revRoas }),
      { umsatz: 0, spend: 0, orders: 0, revRoas: 0 },
    );
    const n = cov.length;
    return { m: { umsatz: sum.umsatz / n, spend: sum.spend / n, orders: sum.orders / n, revRoas: sum.revRoas / n }, n, total: periods.length };
  };

  const noDataNote = `Keine Daten vor dem ${deDate(data.since)}.`;

  const mkBlock = (
    title: string,
    curLabel: string, curCaption: string, curPeriod: OvPeriod,
    benchLabel: string, benchCaption: string, benchPeriods: OvPeriod[],
    unit: string,
  ): OverviewBlock => {
    // Current period: clamp to the data span; flag when the start is cut off.
    let cur: OvMetrics | null = null;
    let curClamped = false;
    if (overlaps(curPeriod)) {
      const p = { from: curPeriod.from < data.since ? data.since : curPeriod.from, to: curPeriod.to > data.until ? data.until : curPeriod.to };
      curClamped = p.from !== curPeriod.from;
      cur = metricsOf(p);
    }
    const bench = avgOf(benchPeriods);
    const notes: string[] = [];
    if (curClamped) notes.push(`${curLabel} unvollständig — Datenbeginn ${deDate(data.since)}.`);
    if (!bench) notes.push(noDataNote);
    else if (bench.n < bench.total) notes.push(`Ø aus ${bench.n} von ${bench.total} ${unit} (Datenbeginn ${deDate(data.since)}).`);
    return {
      title,
      curLabel, curCaption,
      benchLabel, benchCaption,
      cur: cur ? cellsOf(cur) : null,
      bench: bench ? cellsOf(bench.m) : null,
      deltas: cur && bench ? deltasOf(cur, bench.m) : OVERVIEW_COLS.map(() => null),
      note: notes.length ? notes.join(' ') : null,
    };
  };

  const a = clampDate(data, anchor);
  const isYesterday = a === lastCompleteDay(data);

  // 1 — anchor day vs. Ø of the last 4 same weekdays
  const wd = d(a).getUTCDay();
  const sameDays = [7, 14, 21, 28].map((n) => ({ from: shiftIso(a, -n), to: shiftIso(a, -n) }));
  const b1 = mkBlock(
    'Tag vs. gleiche Wochentage',
    isYesterday ? 'Gestern' : 'Stichtag', `${WEEKDAYS_DE[wd]}, ${deShort(a)}`, { from: a, to: a },
    `Ø letzte 4 ${WEEKDAYS_DE[wd]}e`, sameDays.map((p) => deShort(p.from)).reverse().join(' · '), sameDays,
    'Wochentagen',
  );

  // 2 — last complete week (Mo–So) vs. Ø of the 4 weeks before it
  const dow = d(a).getUTCDay() || 7; // Mo=1 .. So=7
  const lwEnd = dow === 7 ? a : shiftIso(a, -dow);
  const lwStart = shiftIso(lwEnd, -6);
  const prevWeeks = [1, 2, 3, 4].map((k) => ({ from: shiftIso(lwStart, -7 * k), to: shiftIso(lwEnd, -7 * k) }));
  const b2 = mkBlock(
    'Woche vs. letzte 4 Wochen',
    'Letzte Woche', `KW${isoWeek(d(lwStart))} · ${deShort(lwStart)}–${deShort(lwEnd)}`, { from: lwStart, to: lwEnd },
    'Ø letzte 4 Wochen', `KW${isoWeek(d(prevWeeks[3].from))}–KW${isoWeek(d(prevWeeks[0].from))} · ${deShort(prevWeeks[3].from)}–${deShort(prevWeeks[0].to)}`, prevWeeks,
    'Wochen',
  );

  // 3 + 4 — last complete calendar month
  const ad = d(a);
  let my = ad.getUTCFullYear(), mm = ad.getUTCMonth();
  if (a !== monthEndIso(my, mm)) { mm -= 1; if (mm < 0) { mm = 11; my -= 1; } }
  const lm: OvPeriod = { from: monthStartIso(my, mm), to: monthEndIso(my, mm) };
  const prevMonths = [1, 2, 3, 4].map((k) => {
    let m2 = mm - k, y2 = my;
    while (m2 < 0) { m2 += 12; y2 -= 1; }
    return { from: monthStartIso(y2, m2), to: monthEndIso(y2, m2) };
  });
  const monLabel = (p: OvPeriod) => MONTHS_DE[d(p.from).getUTCMonth()];
  const b3 = mkBlock(
    'Monat vs. letzte 4 Monate',
    'Letzter Monat', `${MONTHS_LONG[mm]} ${my}`, lm,
    'Ø letzte 4 Monate', `${monLabel(prevMonths[3])}–${monLabel(prevMonths[0])} ${d(prevMonths[0].from).getUTCFullYear()}`, prevMonths,
    'Monaten',
  );
  const b4 = mkBlock(
    'Monat vs. Vorjahresmonat',
    'Letzter Monat', `${MONTHS_LONG[mm]} ${my}`, lm,
    'Vorjahresmonat', `${MONTHS_LONG[mm]} ${my - 1}`, [{ from: monthStartIso(my - 1, mm), to: monthEndIso(my - 1, mm) }],
    'Monaten',
  );

  // 5 — YTD vs. the same span of the previous year (Feb 29 → Feb 28)
  const yr = ad.getUTCFullYear();
  const prevSame = a.slice(5) === '02-29' ? `${yr - 1}-02-28` : `${yr - 1}-${a.slice(5)}`;
  const b5 = mkBlock(
    'YTD vs. Vorjahreszeitraum',
    'Jahr bis heute', `1.1.–${deShort(a)} ${yr}`, { from: `${yr}-01-01`, to: a },
    `Vorjahr (gleicher Zeitraum)`, `1.1.–${deShort(prevSame)} ${yr - 1}`, [{ from: `${yr - 1}-01-01`, to: prevSame }],
    'Zeiträumen',
  );

  return [b1, b2, b3, b4, b5];
}
